'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IDegreeFinderProps } from '@/models/degree-finder/IDegreeFinderProps';
import {
  ApplicationType,
  ButtonSizeType,
  ButtonStyleType,
  ChoiceDegreeFinderPage,
  createDropdownOptions,
  DegreeFinderSearchHookProps,
  getDegreeFinderSearchQuery,
  IChoiceDegreeFinder,
  IDropdown,
  ISidebarNavigation,
  SearchHookProps,
  useDegreeFinderSearch,
} from '@into-discovery/browsewebshared';
import colorStyles from '@/styles/utils/variables.module.scss';
import { i18n } from '@/i18n-config';
import { useTranslations } from 'next-intl';
import { usePathname } from '@/navigation';
import { Container } from 'react-bootstrap';
import { AxiosResponse } from 'axios';

type Props = {
  pageProps: IDegreeFinderProps;
};

export default function DegreeFinderProvider({ pageProps }: Props) {
  const pagePathName = usePathname();
  const translate = useTranslations(`Common`);

  const degreeFinderPageSettings = useMemo(
    () => ({
      buttonStyle: ButtonStyleType.Outline,
      buttonSize: ButtonSizeType.Medium,
      buttonCopyColor: colorStyles.primary,
      outlineColor: colorStyles.primary,
      backgroundColor: colorStyles.white,
      hoverBackgroundColor: colorStyles.primaryHover,
      activeBackgroundColor: colorStyles.primaryActive,
      applicationPrimaryColor: colorStyles.primary,
      b2cFlag: true,
      pagePath: pagePathName,
      locales: i18n.locales,
    }),
    [pagePathName],
  );

  const isMoreResultsLoading = useRef<boolean>(false);

  const defaultDegreeFinderSearchParams: SearchHookProps = useMemo(
    () => ({
      page: 1,
      pageSize: pageProps?.defaultPageSize,
      sortOrder: `1`,
      searchApiSettings: {
        baseUrl: `${process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL}/v1`,
        searchTimeOut: 10000,
        b2cFlag: true,
        applicationType: ApplicationType.PartnerPortal,
      },
    }),
    [pageProps?.defaultPageSize],
  );

  const sidebarNavProps: ISidebarNavigation = useMemo(() => {
    const navProps: ISidebarNavigation = {
      topImage: {
        fields: {
          image: pageProps?.brandSidebarNavigation?.topImage?.image || [],
          altText: pageProps?.brandSidebarNavigation?.topImage?.altText || ``,
          size: pageProps?.brandSidebarNavigation?.topImage?.size || ``,
        },
      },
      links: [],
    };

    pageProps?.brandSidebarNavigation?.linksCollection?.links?.forEach((link) => {
      if (link) {
        navProps.links.push({
          fields: {
            urlLink: link.urlLink,
            displayName: link.displayName,
            application: link.application,
          },
        });
      }
    });

    return navProps;
  }, [pageProps?.brandSidebarNavigation]);

  const [pageNumber, setPageNumber] = useState(defaultDegreeFinderSearchParams.page);
  const [degreeFinderKeyword, setDegreeFinderKeyword] = useState(``);
  const [searchInput, setSearchInput] = useState(``);
  const [selectedProgramType, setSelectedProgramType] = useState(``);
  const [selectedBrandName, setSelectedBrandName] = useState(``);

  const isResetDisabled = useMemo(
    () => searchInput.trim() === `` && selectedProgramType === `` && selectedBrandName === ``,
    [searchInput, selectedProgramType, selectedBrandName],
  );

  const searchOptions = useMemo(() => {
    const result: DegreeFinderSearchHookProps & { searchQuery?: string } = {
      sortOrder: defaultDegreeFinderSearchParams.sortOrder,
      page: pageNumber,
      pageSize: pageProps?.defaultPageSize,
      DegreeFinderSearchServiceSettings: defaultDegreeFinderSearchParams.searchApiSettings,
      keyword: degreeFinderKeyword,
      choiceBrandId: pageProps?.brandId,
      degreeFinderKeyword,
      selectedProgramType,
      selectedBrandName,
    };

    result.searchQuery = getDegreeFinderSearchQuery(result);

    return result;
  }, [
    defaultDegreeFinderSearchParams.searchApiSettings,
    defaultDegreeFinderSearchParams.sortOrder,
    degreeFinderKeyword,
    pageNumber,
    pageProps?.brandId,
    pageProps?.defaultPageSize,
    selectedBrandName,
    selectedProgramType,
  ]);

  const allChoiceDegrees = useRef<IChoiceDegreeFinder[]>([]);

  const choiceDegreeSearchDataProcessing = useCallback(
    async (data: AxiosResponse<any, any>) => {
      const results = data?.data || [];
      const searchKeyword = decodeURI(results?.searchOptions?.keywords ?? ``);
      const searchQueryId = results?.searchOptions?.searchServiceId;
      const loadedCourses = results.results;

      let choiceDegrees: IChoiceDegreeFinder[];
      if (isMoreResultsLoading.current) {
        isMoreResultsLoading.current = false;
        choiceDegrees = [...allChoiceDegrees.current, ...loadedCourses];
      } else {
        choiceDegrees = loadedCourses;
      }

      allChoiceDegrees.current = choiceDegrees;

      const allFacets = results?.searchOptions?.facets || [];
      const choiceDegreesProgramType = createDropdownOptions(
        allFacets,
        `ProgramGroup`,
        `All Program Types`,
      );
      const choiceDegreesAffiliatedUniversities = createDropdownOptions(
        allFacets,
        `BrandName`,
        `All Universities`,
      );

      const currentPage = results?.searchOptions?.pagingInfo?.currentPage ?? 0;
      const totalDegrees = results?.searchOptions?.pagingInfo?.total ?? 0;

      const isNextPageAvailable = totalDegrees / pageProps?.defaultPageSize > currentPage;
      const nextPageCount = Math.min(
        totalDegrees - choiceDegrees.length,
        pageProps?.defaultPageSize,
      );

      return {
        searchKeyword,
        searchQueryId,
        choiceDegrees,
        currentPage,
        nextPageCount,
        totalDegrees,
        choiceDegreesProgramType,
        choiceDegreesAffiliatedUniversities,
        isNextPageAvailable,
      };
    },
    [pageProps?.defaultPageSize],
  );

  const {
    data: renderData,
    isLoading: isResultLoading,
    error,
  } = useDegreeFinderSearch(
    searchOptions.searchQuery,
    searchOptions.DegreeFinderSearchServiceSettings,
    {
      searchKeyword: ``,
      searchQueryId: ``,
      choiceDegrees: [],
      currentPage: 1,
      nextPageCount: 0,
      totalDegrees: 0,
      choiceDegreesProgramType: [],
      choiceDegreesAffiliatedUniversities: [],
      isNextPageAvailable: true,
    },
    choiceDegreeSearchDataProcessing,
    [searchOptions.searchQuery],
  );

  const isError = !!error;

  const handleLoadNextPageChange = () => {
    if (renderData?.isNextPageAvailable) {
      isMoreResultsLoading.current = true;
      setPageNumber((prevPage) => prevPage + 1);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    const trimmedValue = value.trim();
    if (trimmedValue.length >= 2) {
      setPageNumber(1);
      setDegreeFinderKeyword(trimmedValue);
      allChoiceDegrees.current = []; // Reset the array when a new search is initiated
    } else if (trimmedValue.length === 0) {
      setPageNumber(1);
      setDegreeFinderKeyword(``);
      allChoiceDegrees.current = []; // Reset the array when a new search is initiated
    }
  };

  const handleProgramTypeChange = (value: string) => {
    setPageNumber(1);
    setSelectedProgramType(value);
    allChoiceDegrees.current = []; // Reset the array when a new search is initiated
  };

  const handleBrandNameChange = (value: string) => {
    setPageNumber(1);
    setSelectedBrandName(value);
    allChoiceDegrees.current = []; // Reset the array when a new search is initiated
  };

  const handleResetFacetsChange = () => {
    setPageNumber(1);
    setDegreeFinderKeyword(``);
    setSearchInput(``);
    setSelectedProgramType(``);
    setSelectedBrandName(``);
    allChoiceDegrees.current = []; // Reset the array when a new search is initiated
  };

  const [initialProgramOptions, setInitialProgramOptions] = useState<IDropdown[]>([]);
  const [initialBrandOptions, setInitialBrandOptions] = useState<IDropdown[]>([]);

  useEffect(() => {
    if (renderData) {
      if (initialProgramOptions.length === 0 && renderData.choiceDegreesProgramType?.length > 0) {
        setInitialProgramOptions(renderData.choiceDegreesProgramType);
      }
      if (
        initialBrandOptions.length === 0 &&
        renderData.choiceDegreesAffiliatedUniversities?.length > 0
      ) {
        setInitialBrandOptions(renderData.choiceDegreesAffiliatedUniversities);
      }
    }
  }, [renderData, initialProgramOptions.length, initialBrandOptions.length]);

  return (
    <Container>
      {isError && (
        <div>
          <h3>Error loading data.</h3>
        </div>
      )}
      <ChoiceDegreeFinderPage
        choiceDegrees={allChoiceDegrees.current} // Use allChoiceDegrees.current instead of renderData?.choiceDegrees
        choiceDegreesProgramType={initialProgramOptions}
        choiceDegreesAffiliatedUniversities={initialBrandOptions}
        brandSidebarNavigation={sidebarNavProps}
        pageHeading={translate(`choiceDegreeFinderCopy.heading`)}
        pageCopyText={
          pageProps?.brandDegreeFinderNote
            ? pageProps?.brandDegreeFinderNote
            : translate(`choiceDegreeFinderCopy.copyText`)
        }
        searchInputValue={searchInput}
        selectedProgramType={selectedProgramType}
        selectedAffiliatedUniversity={selectedBrandName}
        onFacetSearchInputChange={handleSearchInputChange}
        onFacetProgramTypeChange={handleProgramTypeChange}
        onFacetAffiliatedUniversityChange={handleBrandNameChange}
        isNextPageAvailable={renderData?.isNextPageAvailable}
        nextPageCount={renderData?.nextPageCount}
        isResultLoading={isResultLoading}
        isResetDisabled={isResetDisabled}
        onResetFacetsChange={handleResetFacetsChange}
        onLoadNextPageChange={handleLoadNextPageChange}
        degreeFinderPageSettings={degreeFinderPageSettings}
      />
    </Container>
  );
}
